import { API_BASE_URL } from '../config/api';

export interface UserSettings {
  name: string;
  email: string;
  phone?: string;
  roles: string[];
  primaryEstate: string;
  notifications: {
    email: boolean;
    inApp: boolean;
    sms: boolean;
  };
}

export interface UpdateSettingsResponse {
  success: boolean;
  message?: string;
  settings?: UserSettings;
}

export interface AccountDeletionStatus {
  success: boolean;
  message: string;
  hasPlannerProfile?: boolean;
  plannerProfileDeletedAt?: string | null;
  executorInvitesCount: number;
  canDeletePlannerProfile: boolean;
  canDeleteFullAccount: boolean;
}

class SettingsService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('account-token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }

  async updateProfile(data: Partial<UserSettings>): Promise<UpdateSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<UpdateSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/password`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update password:', error);
      throw error;
    }
  }

  async updateNotifications(preferences: { email: boolean; inApp: boolean; sms: boolean }): Promise<UpdateSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/notifications`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  }

  async downloadData(): Promise<Blob> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/data`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to download data:', error);
      throw error;
    }
  }

  async getDeletionStatus(): Promise<AccountDeletionStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/deletion-status`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get deletion status:', error);
      throw error;
    }
  }

  async deletePlannerProfile(): Promise<UpdateSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/planner-profile`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete planner profile:', error);
      throw error;
    }
  }

  async deleteAccount(): Promise<UpdateSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete account:', error);
      throw error;
    }
  }
}

export const settingsService = new SettingsService();
